import useLiveSanityContent, {
  UseLiveSanityContentResponse
} from "src/ssc-api/useLiveSanityContent";
import { buildCruiseName } from "src/utils/cruise";
import { SanityImageType } from "@silversea-ssc/ui-components-react";
import { EnhancedAvailablePriceWithFareCode } from "src/ssc-api/prices/types";
import { match } from "ts-pattern";

type RemoteCruiseCardContent_Ship = {
  _id: string;
  name: string;
  version: {
    suites: RemoteCruiseCardContent_Suite[];
  };
};

export type RemoteCruiseCardContent_Suite = {
  // codes: string[];
  configurations: {
    code: string;
  }[];
  category: {
    name: {
      localized: string;
    };
  };
};

export type RemoteCruiseCardContent = {
  cruise: {
    _id: string;
    cardImage: { asset: { _ref: string } };
    card?: { image?: { asset: { _ref: string } } };
    data?: {
      departurePort?: {
        data?: {
          name?: {
            localized?: string;
          };
        };
      };
      arrivalPort?: {
        data?: {
          name?: {
            localized?: string;
          };
        };
      };
      departureDate: string;
      arrivalDate: string;
      days: number;
      ship: RemoteCruiseCardContent_Ship;
    };
  };
  specialVoyage: {
    _id: string;
    cardImage: { asset: { _ref: string } };
    card?: { image?: { asset: { _ref: string } } };
    data?: {
      departurePort?: {
        data?: {
          name?: {
            localized?: string;
          };
        };
      };
      arrivalPort?: {
        data?: {
          name?: {
            localized?: string;
          };
        };
      };
      departureDate: string;
      arrivalDate: string;
      days: number;
      ship: RemoteCruiseCardContent_Ship;
    };
  };
};

const shipQuery = `
ship->{
  _id,
  name,
  "version": versions[(validFrom <= ^.^.departureDate)] | order(validFrom desc)[0] {
    _key,
    suites[]{
      configurations[]{
        code
      },
      category->{
        name{"localized": en}
      }
    }
  }
}
`;

export const useCruiseCard = (
  cruiseCode?: string
): UseLiveSanityContentResponse<RemoteCruiseCardContent> => {
  const query = cruiseCode
    ? `
    {
      "cruise": *[_type == "cruise" && defined(data) && data._ref == (*[_type=="cruiseData" && cruiseCode == "${cruiseCode}"][0]{_id}._id)]
      {
        _id,
        data -> {
          departureDate,
          arrivalDate,
          departurePort->{data->{name{"localized": en}}},
          arrivalPort->{data->{name{"localized": en}}},
          days,
        ${shipQuery}
        },
        cardImage{asset}
      }[0],

      "specialVoyage": *[_type == "specialVoyage" && defined(data) && data._ref == (*[_type=="specialVoyageData" && cruiseCode == "${cruiseCode}"][0]{_id}._id)]
      {
        _id,
        data -> {
          departureDate,
          arrivalDate,
          departurePort->{data->{name{"localized": en}}},
          arrivalPort->{data->{name{"localized": en}}},
          days,
        ${shipQuery}
        },
        card {
          image{asset}
        }
      }[0]
    }
  `
    : undefined;

  return useLiveSanityContent<RemoteCruiseCardContent>(query);
};

// eslint-disable-next-line import/no-unused-modules
export const getSuite = <
  T extends {
    configurations: RemoteCruiseCardContent_Suite["configurations"];
  }
>(
  suiteSlug: string,
  suites: T[]
): T | undefined => {
  if (!suiteSlug) return undefined;
  return suites.find((s) =>
    s.configurations.map((c) => c.code).includes(suiteSlug)
  );
};

export const getCardSummaryContent = (
  cruise?: RemoteCruiseCardContent["cruise"],
  specialVoyage?: RemoteCruiseCardContent["specialVoyage"]
): { title: string; image?: SanityImageType } => {
  const cruiseOrSpecialVoyage = cruise || specialVoyage;
  const title = cruiseOrSpecialVoyage
    ? buildCruiseName(
        cruiseOrSpecialVoyage?.data?.departurePort?.data?.name?.localized,
        cruiseOrSpecialVoyage?.data?.arrivalPort?.data?.name?.localized
      )
    : "";
  const imageRef =
    cruise?.cardImage?.asset?._ref || specialVoyage?.card?.image?.asset?._ref;
  const image = imageRef
    ? {
        asset: {
          _id: imageRef
        }
      }
    : undefined;

  return { title, image };
};

export const priceToNumber = (
  price: EnhancedAvailablePriceWithFareCode
): number | undefined =>
  match(price)
    .with({ type: "waitlist" }, () => undefined)
    .with({ type: "price" }, (price) => price.amount)
    .with({ type: "discounted" }, (price) => price.originalAmount)
    .exhaustive();
